/**
 * 🔊 CoachAudioButton — play the AI coach greeting aloud via OpenAI TTS.
 *
 * Shared by the Interactive Kitchen and Interactive Gym pages. Delegates
 * to /api/tts/synthesize with a domain hint ('kitchen' or 'gym') that
 * routes to the correct default voice (nova for kitchen, echo for gym).
 *
 * Canon character → voice map (Feb 2026):
 *   David     → onyx
 *   Roc       → echo
 *   Cassandra → nova
 *   Ledger    → fable
 *
 * v1: single "play greeting" button that reads the full block aloud.
 * Per-character voice attribution ships when speaker tags land on lines.
 */
import { useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CoachAudioButton({ greetingLines, domain = "kitchen", ...rest }) {
  const [state, setState] = useState("idle"); // idle | loading | playing
  const audioRef = useRef(null);

  const combinedText = useMemo(() => {
    return (greetingLines || []).filter(Boolean).join(" ");
  }, [greetingLines]);

  const play = async () => {
    if (!combinedText) return;
    if (state === "playing" && audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setState("idle");
      return;
    }
    setState("loading");
    try {
      const r = await axios.post(`${API}/tts/synthesize`, {
        text: combinedText,
        domain,
        model: "tts-1",
      });
      const b64 = r.data?.audio_base64;
      const mime = r.data?.mime_type || "audio/mpeg";
      if (!b64) throw new Error("No audio returned.");
      const src = `data:${mime};base64,${b64}`;
      if (!audioRef.current) audioRef.current = new Audio();
      audioRef.current.src = src;
      audioRef.current.onended = () => setState("idle");
      audioRef.current.onerror = () => setState("idle");
      await audioRef.current.play();
      setState("playing");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't play the greeting.");
      setState("idle");
    }
  };

  const label = state === "loading"
    ? "Loading…"
    : state === "playing"
      ? "Stop"
      : "Play Greeting";
  const emoji = state === "playing" ? "⏹" : "🔊";

  return (
    <button
      onClick={play}
      disabled={!combinedText || state === "loading"}
      data-testid={rest["data-testid"] || "coach-audio-btn"}
      className={`flex items-center gap-1.5 rounded-full px-2.5 py-1 border transition text-[10px] font-black uppercase tracking-widest ${
        state === "playing"
          ? "border-sky-300/60 bg-sky-500/25 text-sky-100"
          : "border-neutral-700 bg-black/50 text-neutral-200 hover:border-emerald-500/70 hover:text-emerald-200"
      } disabled:opacity-50 disabled:cursor-not-allowed`}
      title={`${label} · ${domain === "gym" ? "Roc / Echo voice" : "Cassandra / Nova voice"}`}
    >
      <span className="text-sm">{emoji}</span>
      <span>{label}</span>
    </button>
  );
}
